export interface OiReport {
    testName: string;
    startTime: string;
    endTime: string;
    elementName: string;
    comment: string;
    tags: string[];
    taggedUsers: string[];
    taggedElements: string[];
    referencedDocuments: FileReference[];
    dataMatrixCodes: string[];
    taggedElementsComments: string[];
    taggedElementsStart: string[];
    taggedElementsEnd: string[];
    author: string;
    testSelection: string;
    guid: string;
    isDraft: boolean;
    isCloned?: boolean;
    oiType: number;
    createdAt?: string;
}

export interface FileReference {
    uuid: string;
    fileName: string;
}

const normalizeReferencedDocuments = (raw: unknown): FileReference[] => {
    if (!Array.isArray(raw)) return [];

    const normalized: FileReference[] = [];
    for (const item of raw) {
        if (!item) continue;

        if (typeof item === "string") {
            const fileName = item.includes("_") ? item.split('_').slice(1).join('_') : item;
            normalized.push({ uuid: item, fileName });
            continue;
        }

        if (typeof item === "object") {
            const maybe = item as Record<string, unknown>;
            const uuid = (maybe.uuid ?? maybe.id ?? maybe.fileId) as unknown;
            const fileName = (maybe.fileName ?? maybe.name ?? maybe.filename) as unknown;

            if (typeof uuid === "string" && typeof fileName === "string") {
                normalized.push({ uuid, fileName });
                continue;
            }
            if (typeof uuid === "string" && typeof fileName !== "string") {
                normalized.push({ uuid, fileName: uuid });
                continue;
            }
        }
    }

    return normalized;
};



export function createEmptyOiReport(): OiReport {
    return {
        testName: "",
        startTime: "",
        endTime: "",
        elementName: "",
        comment: "",
        tags: [],
        taggedUsers: [],
        taggedElements: [],
        referencedDocuments: [],
        dataMatrixCodes: [],
        taggedElementsComments: [],
        taggedElementsStart: [],
        taggedElementsEnd: [],
        author: "",
        testSelection: "",
        guid: "",
        isDraft: false,
        oiType: 1
    };
}


export function validateOiReport(report: OiReport): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!report.testName.trim()) {
        errors.push("Test name is required");
    }

    if (!report.testSelection.trim()) {
        errors.push("Test selection is required");
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}


export function toPoiDataFormat(report: OiReport): Record<string, any> {
    const referencedDocuments = (Array.isArray(report.referencedDocuments) ? (report.referencedDocuments as any[]) : [])
        .map((doc) => {
            if (!doc) return null;
            if (typeof doc === "string") return doc;
            if (typeof doc === "object" && typeof (doc as any).uuid === "string") return (doc as any).uuid;
            return null;
        })
        .filter((v): v is string => typeof v === "string" && v.trim() !== "");

    const payload: Record<string, any> = {
        testName: report.testName,
        startTime: report.startTime,
        endTime: report.endTime,
        elementName: report.elementName,
        comment: report.comment,
        tags: report.tags,
        taggedUsers: report.taggedUsers,
        taggedElements: report.taggedElements,
        dataMatrixCodes: report.dataMatrixCodes,
        // Send both casings for compatibility with backends that expect PascalCase.
        referencedDocuments,
        ReferencedDocuments: referencedDocuments,
        taggedElementsComments: report.taggedElementsComments,
        taggedElementsStart: report.taggedElementsStart,
        taggedElementsEnd: report.taggedElementsEnd,
        author: report.author,
        testSelection: report.testSelection,
        isDraft: report.isDraft,
        oiType: report.oiType
    };

    if (report.guid && report.guid.trim() !== '') {
        payload.guid = report.guid;
    }


    return payload;
}

export function fromPoiDataFormat(poiData: Record<string, any>): OiReport {
    const getProperty = (obj: Record<string, any>, possibleNames: string[], defaultValue: any = "") => {
        for (const name of possibleNames) {
            if (obj.hasOwnProperty(name) && obj[name] !== undefined && obj[name] !== null) {
                return obj[name];
            }
        }
        return defaultValue;
    };
    const result: OiReport = {
        testName: getProperty(poiData, ["TestName", "testName", "test_name", "Test_Name"]),
        startTime: getProperty(poiData, ["StartTime", "startTime", "start_time", "Start_Time"]),
        endTime: getProperty(poiData, ["EndTime", "endTime", "end_time", "End_Time"]),
        elementName: getProperty(poiData, ["ElementName", "elementName", "element_name", "Element_Name"]),
        comment: getProperty(poiData, ["Comment", "comment", "Comment"]),
        tags: (() => {
            const rawTags = getProperty(poiData, ["Tags", "tags", "Tags"], []);
            if (Array.isArray(rawTags)) {
                return rawTags.flatMap(tag =>
                    typeof tag === 'string' ? tag.split(',').map(t => t.trim()).filter(t => t !== '') : [tag]
                );
            }
            return [];
        })(),
        taggedUsers: getProperty(poiData, ["TaggedUsers", "taggedUsers", "tagged_users", "Tagged_Users"], []),
        taggedElements: getProperty(poiData, ["TaggedElements", "taggedElements", "tagged_elements", "Tagged_Elements"], []),
        referencedDocuments: normalizeReferencedDocuments(
            getProperty(poiData, ["ReferencedDocuments", "referencedDocuments", "referenced_documents", "Referenced_Documents"], [])
        ),
        dataMatrixCodes: getProperty(poiData, ["DataMatrixCodes", "dataMatrixCodes", "data_matrix_codes", "Data_Matrix_Codes"], []),
        taggedElementsComments: getProperty(poiData, ["TaggedElementsComments", "taggedElementsComments", "tagged_elements_comments", "Tagged_Elements_Comments"], []),
        taggedElementsStart: getProperty(poiData, ["TaggedElementsStart", "taggedElementsStart", "tagged_elements_start", "Tagged_Elements_Start"], []),
        taggedElementsEnd: getProperty(poiData, ["TaggedElementsEnd", "taggedElementsEnd", "tagged_elements_end", "Tagged_Elements_End"], []),
        author: getProperty(poiData, ["Author", "author", "Author"]),
        testSelection: getProperty(poiData, ["TestSelection", "testSelection", "test_selection", "Test_Selection"]),
        guid: getProperty(poiData, ["Guid", "guid", "id", "Id", "ID", "GUID"]),
        isDraft: getProperty(poiData, ["IsDraft", "isDraft", "is_draft", "Is_Draft"], false),
        oiType: getProperty(poiData, ["OiType", "oiType", "oi_type", "Oi_Type"], 1),
        createdAt: getProperty(poiData, ["CreatedAt", "createdAt"], "") || undefined
    };


    return result;
}